import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import {
  ASSISTANT_ERROR_CODES,
  AssistantActionResult,
  AssistantConfig,
  AssistantErrorCode,
  AssistantFeedbackRequest,
  AssistantMessageRequest,
  AssistantReply,
  RECEIPT_ERROR_CODES,
  ReceiptErrorCode,
  ReceiptScanResult,
} from '../models/assistant.model';

/** The calls of Ezri's API (AssistantController). */
@Injectable({ providedIn: 'root' })
export class AssistantService {
  private readonly http = inject(HttpClient);
  private readonly API = `${environment.apiUrl}/assistant`;

  getConfig(): Observable<AssistantConfig> {
    return this.http.get<AssistantConfig>(`${this.API}/config`, { withCredentials: true });
  }

  sendMessage(request: AssistantMessageRequest): Observable<AssistantReply> {
    return this.http.post<AssistantReply>(`${this.API}/messages`, request, { withCredentials: true });
  }

  confirmAction(actionId: string): Observable<AssistantActionResult> {
    return this.http.post<AssistantActionResult>(
      `${this.API}/actions/${encodeURIComponent(actionId)}/confirm`, {}, { withCredentials: true });
  }

  cancelAction(actionId: string): Observable<AssistantActionResult> {
    return this.http.post<AssistantActionResult>(
      `${this.API}/actions/${encodeURIComponent(actionId)}/cancel`, {}, { withCredentials: true });
  }

  /**
   * Uploads a receipt and reads it (ReceiptsController). Not retried by
   * HttpErrorInterceptor once it skips /api/receipts: a retry is a second paid
   * scan, and 429/503 there are answers.
   */
  scanReceipt(file: Blob, fileName: string, uiLanguage?: string): Observable<ReceiptScanResult> {
    const form = new FormData();
    form.append('file', file, fileName);
    if (uiLanguage) form.append('uiLanguage', uiLanguage);
    return this.http.post<ReceiptScanResult>(`${environment.apiUrl}/receipts/scan`, form, { withCredentials: true });
  }

  sendFeedback(request: AssistantFeedbackRequest): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${this.API}/feedback`, request, { withCredentials: true });
  }

  /** The `{ code, message }` body's code when it is one the UI words; otherwise 'generic'. */
  static errorCode(error: unknown): AssistantErrorCode {
    return AssistantService.known(error, ASSISTANT_ERROR_CODES);
  }

  /** As errorCode, for the receipt scan's refusals. A request too large for the server is file_too_large. */
  static receiptErrorCode(error: unknown): ReceiptErrorCode {
    if (error instanceof HttpErrorResponse && error.status === 413) return 'file_too_large';
    return AssistantService.known(error, RECEIPT_ERROR_CODES);
  }

  private static known<T extends string>(error: unknown, codes: readonly T[]): T {
    const body = error instanceof HttpErrorResponse ? error.error : null;
    let code: unknown = body && typeof body === 'object' ? (body as { code?: unknown }).code : null;
    if (typeof body === 'string') {
      try { code = JSON.parse(body)?.code; } catch { code = null; }
    }
    return typeof code === 'string' && (codes as readonly string[]).includes(code) ? code as T : 'generic' as T;
  }
}
